// src/layout/AppTopbar.tsx
import { classNames } from "primereact/utils";
import { Badge } from "primereact/badge";
import { BreadCrumb } from "primereact/breadcrumb";
import "primeicons/primeicons.css";

import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { LayoutContext } from "./context/layoutcontext";
import { AppTopbarRef } from "../../types/layout";
import { useNavigate, useLocation } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { Menu } from "primereact/menu";
import { useTranslation } from "react-i18next";
import i18n from "../../localisation/i18n";
import AuthService from "../../services/autentication/auth.service";
import { useAppSelector } from "../../redux/hooks";
import UserService from "../../services/user/user.service";
import RoleService from "../../services/role/role-service";
import { OverlayPanel } from "primereact/overlaypanel";
import { ExecutiveInfoDTO } from "../../models/CustomerExecutive/CustomerExecutive-model";
import CustomerExecutiveService from "../../services/CustomerExecutives/CustomerExecutivesservice";
import { Avatar } from "primereact/avatar";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import orderServices from "../../services/orders/orderservice";
import { Toast } from "primereact/toast";
import CustomerServComplaintRegister from "../Complaint/CustomerServComplaintRegister";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import ProductService from "../../services/products/products.service";
import { ProductPriceExport } from "../admin/product-price-export";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import axios from "axios";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";

// Define menu item types
interface MenuItemBase {
  id: string;
  name: string;
  icon?: string;
  title?: string;
}

interface MenuItemWithPath extends MenuItemBase {
  path: string;
  action?: never;
  items?: never;
}

interface MenuItemWithAction extends MenuItemBase {
  action: () => void;
  path?: never;
  items?: never;
}

interface MenuItemWithSubmenu extends MenuItemBase {
  items: MenuItem[];
  path?: never;
  action?: never;
}

type MenuItem = MenuItemWithPath | MenuItemWithAction | MenuItemWithSubmenu;

// Search result interface
interface SearchMenuItem {
  id: string;
  name: string;
  path?: string;
  mainTab: MainTabKey;
  category: string;
}


const MEGA_MENU_CSS = `
.mega-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1100;
}

.mega-container {
  position: fixed;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 1200px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  padding: 16px 24px 24px;
  max-height: calc(90vh - 64px);
  display: flex;
  flex-direction: column;
}

.mega-main-tabs {
  display: flex;
  gap: 16px;
  border-bottom: 1px solid #e0e0e0;
  margin: 0 -24px 16px;
  padding: 8px 24px 8px;
  align-items: center;
  justify-content: space-between;
  overflow: visible;
}

.mega-tabs-left {
  display: flex;
  gap: 32px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.mega-tabs-left::-webkit-scrollbar {
  display: none;
}

.mega-search-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 320px;
  flex-shrink: 0;
}

.mega-search-container {
  position: relative;
  width: 100%;
}

.mega-search-autocomplete {
  width: 100%;
}

.mega-search-autocomplete .p-autocomplete-input {
  width: 100%;
  padding: 6px 10px 6px 32px !important;
  border-radius: 999px;
  border: 1px solid #ced4da;
  font-size: 0.85rem;
  outline: none;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.mega-search-autocomplete .p-autocomplete-input:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13,110,253,0.25);
}

.mega-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.9rem;
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.mega-main-tab {
  padding: 12px 0 10px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #37474f;
  position: relative;
  border: none;
  background: transparent;
  flex-shrink: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.mega-main-tab:hover {
  color: #094e28ff;
}

.mega-main-tab-active {
  color: #094e28ff;
  font-weight: 700;
}

.mega-main-tab-active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 3px;
  background: #014e13ff;
}

.mega-close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.4rem;
  color: #495057;
  padding: 4px 8px;
  margin-left: 8px;
  flex-shrink: 0;
  transition: color 0.2s ease, transform 0.2s ease;
}

.mega-close-btn:hover {
  color: #000;
  transform: scale(1.1);
}

.mega-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

.mega-body::-webkit-scrollbar {
  width: 6px;
}

.mega-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.mega-body::-webkit-scrollbar-thumb {
  background: #c5cae9;
  border-radius: 10px;
}

.mega-body::-webkit-scrollbar-thumb:hover {
  background: #9fa8da;
}

.mega-grid-single {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.mega-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.mega-section-title {
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 8px;
  color: #1e9706ff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.mega-subtitle {
  font-size: 0.85rem;
  font-weight: 800;
  margin: 10px 0 6px;
  color: #77a475ff;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.mega-link {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 0.875rem;
  color: #263238;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  border-radius: 4px;
  transition: background 0.2s ease, color 0.2s ease;
}

.mega-link:hover {
  background: #f5f5f5;
  color: #0d47a1;
}

.account-overlay-fixed {
  position: fixed !important;
  z-index: 1200;
  width: 280px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.account-overlay-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1199;
}

.account-menu-item {
  padding: 12px 14px;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s ease, color 0.2s ease;
  margin-bottom: 4px;
  font-size: 0.9rem;
  color: #495057;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  display: flex;
  align-items: center;
}

.account-menu-item:hover {
  background: #f8f9fa;
  color: #094e28ff;
}

.account-menu-item-logout {
  color: #dc3545;
}

.account-menu-item-logout:hover {
  background: #fff5f5;
  color: #dc3545;
}

@media (max-width: 1024px) {
  .mega-container {
    width: calc(100% - 24px);
    max-width: 100%;
  }

  .mega-grid-single {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .mega-grid-4 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .mega-main-tab {
    font-size: 0.875rem;
    padding: 10px 0 8px;
  }

  .mega-tabs-left {
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .mega-container {
    top: 60px;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: none;
    height: calc(100vh - 60px);
    border-radius: 0;
    padding: 12px 16px 16px;
    max-height: none;
  }

  .mega-main-tabs {
    margin: 0 -16px 12px;
    padding: 8px 16px 8px;
    gap: 12px;
  }

  .mega-tabs-left {
    gap: 20px;
  }

  .mega-main-tab {
    font-size: 0.85rem;
    padding: 10px 0 8px;
  }

  .mega-search-wrapper {
    max-width: 200px;
  }

  .mega-close-btn {
    font-size: 1.3rem;
    margin-left: 4px;
  }

  .mega-body {
    flex: 1;
    overflow-y: auto;
    padding-right: 4px;
  }

  .mega-grid-single,
  .mega-grid-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .mega-section-title {
    font-size: 0.85rem;
    margin-bottom: 6px;
  }

  .mega-link {
    padding: 8px 6px;
    font-size: 0.85rem;
  }

  .mega-subtitle {
    font-size: 0.8rem;
    margin: 8px 0 4px;
  }

  .account-overlay-fixed {
    width: calc(100% - 32px);
    max-width: 320px;
    left: 16px !important;
    right: 16px !important;
    top: auto !important;
    bottom: 16px !important;
  }
}

@media (max-width: 480px) {
  .mega-container {
    padding: 8px 12px 12px;
  }

  .mega-main-tabs {
    margin: 0 -12px 8px;
    padding: 6px 12px 8px;
    gap: 8px;
  }

  .mega-tabs-left {
    gap: 16px;
  }

  .mega-main-tab {
    font-size: 0.8rem;
    padding: 8px 0 6px;
  }

  .mega-grid-single,
  .mega-grid-4 {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mega-section-title {
    font-size: 0.8rem;
  }

  .mega-link {
    font-size: 0.8rem;
    padding: 8px 4px;
  }
}

.hide-on-desktop {
  display: none;
}

@media (max-width: 992px) {
  .hide-on-desktop {
    display: inline-flex !important;
  }
}

.search-result-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.search-result-item:hover {
  background: #f8f9fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.search-result-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.75rem;
}

.search-result-tab {
  color: #0d6efd;
  font-weight: 600;
  text-transform: uppercase;
}

.search-result-category {
  color: #6c757d;
}

.p-autocomplete-panel {
  max-height: 400px;
  overflow-y: auto;
  z-index: 1150 !important;
}

/* Mobile menu popup styling */
#popup_menu_left {
  z-index: 1200 !important;
}

#popup_menu_left.p-menu {
  z-index: 1200 !important;
}

.p-menu {
  z-index: 1200 !important;
}

@media (max-width: 768px) {
  #popup_menu_left {
    z-index: 1200 !important;
  }
  
  #popup_menu_left.p-menu {
    z-index: 1200 !important;
  }
  
  .p-menu {
    z-index: 1200 !important;
  }
}
`;

type MainTabKey = "FARM2ME" | "DESIRI" | "REPORTS" | "VENDORS" | "CHALLAN" | "SETTINGS";

const AppTopbar = forwardRef<AppTopbarRef>((props, ref) => {
  const { t } = useTranslation();
  const { setBreadcrumbModel, setOnBreadcrumbClick } = useContext(LayoutContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const menubuttonRef: any = useRef(null);
  const topbarmenuRef: any = useRef(null);
  const topbarmenubuttonRef: any = useRef(null);
  const langMenu: any = useRef(null);
  const menuLeft: any = useRef(null);
  const searchDropdownRef: any = useRef(null);
  const isUserLoggedIn: boolean = AuthService.isUserLoggedIn();
  const [isComplaintSubmenuOpen, setIsComplaintSubmenuOpen] = useState(false);
  const count = useAppSelector((state) => state.counter);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [executiveInfo, setExecutiveInfo] = useState<ExecutiveInfoDTO | null>(null);
  const executiveOverlayRef = useRef<OverlayPanel | null>(null);
  const [complaintDialogVisible, setComplaintDialogVisible] = useState(false);
  const toastRef = useRef<Toast>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [recipientPhone, setRecipientPhone] = useState("");
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState<MainTabKey>("FARM2ME");
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredMenuItems, setFilteredMenuItems] = useState<SearchMenuItem[]>([]);
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const handleNavigateHome = () => {
    setBreadcrumbModel?.([]);
    navigate(RouteConstant.products);
  };

const handleCallMeBack = async () => {
  if (currentUser?.userID) {
    try {
      await orderServices.sendWhatsAppTemplateMessage(
        currentUser.userID,
        'callback_dual_notification'
      );

        toastRef.current?.show({
          severity: "success",
          summary: "Request Sent",
          detail: "Call Me Back request has been initiated.",
          life: 3000,
        });
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorMessage = error.response?.data;

        if (typeof errorMessage === 'string' && errorMessage.includes('Executive mapping not found')) {
          toastRef.current?.show({
            severity: 'warn',
            summary: 'Request Failed',
            detail: 'No executive assigned to your account.',
            life: 3000,
          });
        } else {
          toastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong while sending the request.',
            life: 3000,
          });
        }
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Unexpected Error',
          detail: 'An unknown error occurred.',
          life: 3000,
        });
      }
    }
  } else {
    toastRef.current?.show({
      severity: 'warn',
      summary: 'Missing Info',
      detail: 'Unable to initiate request. User ID not found.',
      life: 3000,
    });
  }
};

const handleProductPriceExport = async () => {
  try {
    let productsArray: any[] = [];

    if (isAdmin) {
      const response = await ProductService.getAllAdminProducts();
      productsArray = Array.isArray(response) ? response : response.data || [];
    } else if (ProductStorageHelpers.GetUserId()) {
      const response = await ProductService.getAllProducts1();
      productsArray = Array.isArray(response) ? response : response.data || [];
    } else {
      const response = await ProductService.getAllProducts();
      productsArray = Array.isArray(response) ? response : response.data || [];
    }

    if (productsArray.length === 0) {
      console.warn("No products available to export.");
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Products',
        detail: 'No products available to export.',
        life: 3000
      });
      return;
    }

    ProductPriceExport(productsArray);
    toastRef.current?.show({
      severity: 'success',
      summary: 'Export Successful',
      detail: 'Product list exported successfully!',
      life: 3000
    });
  } catch (error) {
    console.error("Error exporting product price:", error);
    toastRef.current?.show({
      severity: 'error',
      summary: 'Export Failed',
      detail: 'Failed to export product list.',
      life: 3000
    });
  }
};

const handleWhatsAppReferral = async () => {
  try {
    const senderUserId = ProductStorageHelpers.GetUserId() || 0; 
    const productsArray = await ProductService.getAllProducts();
    const pdfBlob = await ProductPriceExport(productsArray);
    const pdfFile = new File([pdfBlob], 'Product_list.pdf', { type: 'application/pdf' });

    const formData = new FormData();
    formData.append('pdf', pdfFile);

    const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT?.replace(/\/$/, '') || '';

    const uploadResponse = await fetch(`${BASE_URL}/api/whatsapp/upload-pdf`, {
      method: 'POST',
      body: formData
    });

    if (!uploadResponse.ok) {
      throw new Error('Upload failed');
    }

    const { fileUrl: pdfUrl } = await uploadResponse.json(); 

    const encodedUrl = encodeURIComponent(pdfUrl);
    const messageType = `product_price_list_referral|${recipientPhone}|${encodedUrl}`;

    await orderServices.sendWhatsAppTemplateMessage(senderUserId, messageType);

    toastRef.current?.show({
      severity: 'success',
      summary: 'Message Sent',
      detail: 'WhatsApp message sent successfully!',
      life: 3000
    });

    setTimeout(() => setShowWhatsAppModal(false), 100);
    setRecipientPhone("");
  } catch (error) {
    console.error("Error sending WhatsApp referral:", error);
    toastRef.current?.show({
      severity: 'error',
      summary: 'Send Failed',
      detail: 'Failed to send WhatsApp message.',
      life: 3000
    });
  }
};

  useEffect(() => {
    const adminCheck = ProductStorageHelpers.IsAdminUser();
    setIsAdmin(adminCheck);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (AuthService.isUserLoggedIn()) {
        const user = await UserService.getCurrentUser();
        setCurrentUser(user);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (AuthService.isUserLoggedIn()) {
        const user = await UserService.getCurrentUser();
        setCurrentUser(user);

        if (RoleService.isCustomer()) {
          const info = await CustomerExecutiveService.getAssignedExecutive();
          setExecutiveInfo(info);
        }
      }
    };
    fetchData();
  }, []);

  // Breadcrumb mapping for menu routes
  const breadcrumbMap: Record<string, Array<{ label: string }>> = {
    // Farm2Me > Orders & Billing
    [RouteConstant.newinvoice]: [{ label: "Farm2Me" }, { label: "Orders & Billing" }, { label: "Order Details" }],
    [RouteConstant.newGenerateInvoice]: [{ label: "Farm2Me" }, { label: "Orders & Billing" }, { label: "Generate Invoice" }],
    [RouteConstant.canceledOrders]: [{ label: "Farm2Me" }, { label: "Orders & Billing" }, { label: "Cancelled Orders" }],
    [RouteConstant.packingOrders]: [{ label: "Farm2Me" }, { label: "Orders & Billing" }, { label: "Update Bin No" }],
    [RouteConstant.binOrders]: [{ label: "Farm2Me" }, { label: "Orders & Billing" }, { label: "View Bin No Details" }],
    
    // Farm2Me > External Complaints & QA
    [RouteConstant.ComplaintRegisters]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "External Complaints" }],
    [RouteConstant.ComplaintMain]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "External Actions for QA" }],
    [RouteConstant.ComplaintActionMain]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "View All External Complaints" }],
    [RouteConstant.ViewAllStatus]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "Complaint Status" }],
    [RouteConstant.ComplaintType]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "Complaint Types" }],
    [RouteConstant.viewAllCategories]: [{ label: "Farm2Me" }, { label: "External Complaints & QA" }, { label: "Complaint Category" }],
    
    // Farm2Me > Delivery & Route Management
    [RouteConstant.Geomap]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Smart Map" }],
    [RouteConstant.Geomapsearch]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Today's Map" }],
    [RouteConstant.deliveryDetails]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Delivery Report" }],
    [RouteConstant.OrderDeliverySummary]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Order Delivery Tracking" }],
    [RouteConstant.DeliveryCashMain]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Delivery Executive Daily Collection" }],
    [RouteConstant.storeCashMain]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Store Cash Collection" }],
    [RouteConstant.AccountsCashMain]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Accounts Cash Collection" }],
    [RouteConstant.CashReportMain]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Where is My Cash" }],
    [RouteConstant.ViewAllChallan]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "View Challan" }],
    [RouteConstant.assignroutecode]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Assign Route Code" }],
    [RouteConstant.openorders]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Unassigned Orders" }],
    [RouteConstant.NewCountry]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Country" }],
    [RouteConstant.addState]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "State" }],
    [RouteConstant.addDistrict]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "District" }],
    [RouteConstant.addSubdistrict]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Sub District" }],
    [RouteConstant.addArea]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Area and Pincode" }],
    [RouteConstant.customerAddress]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "View All Address" }],
    [RouteConstant.deliveryAddress]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "Delivery Address" }],
    [RouteConstant.viewAllRouteCodes]: [{ label: "Farm2Me" }, { label: "Delivery & Route Management" }, { label: "View Route Codes" }],
    
    // Farm2Me > POS & Additional
    [RouteConstant.viewAllProducts]: [{ label: "Farm2Me" }, { label: "POS & Additional" }, { label: "View All Products" }],
    [RouteConstant.positems]: [{ label: "Farm2Me" }, { label: "POS & Additional" }, { label: "POS Inventory" }],
    [RouteConstant.posmovements]: [{ label: "Farm2Me" }, { label: "POS & Additional" }, { label: "POS Movements" }],
    [RouteConstant.outwardChallan]: [{ label: "Farm2Me" }, { label: "POS & Additional" }, { label: "Outward Challan" }],
    
    // Farm2Me > Customer & CRM
    [RouteConstant.editUsers]: [{ label: "Farm2Me" }, { label: "Customer & CRM" }, { label: "View All Customers" }],
    [RouteConstant.AssignExecutive]: [{ label: "Farm2Me" }, { label: "Customer & CRM" }, { label: "Assign Customer Executives" }],
    [RouteConstant.customerFollowUpReport]: [{ label: "Farm2Me" }, { label: "Customer & CRM" }, { label: "Customer Follow Up" }],
    
    // Desiri > Desiri Master Data
    [RouteConstant.desiriUsers]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Desiri User Profile" }],
    [RouteConstant.desiriBulls]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Bull" }],
    [RouteConstant.desiriGaana]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Gaana" }],
    [RouteConstant.ViewRawMaterials]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Add Raw Materials" }],
    [RouteConstant.AddVehicle]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Vehicle Details" }],
    [RouteConstant.desiriRoles]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Roles & Designations" }],
    [RouteConstant.BullStatistics]: [{ label: "Desiri" }, { label: "Desiri Master Data" }, { label: "Bull Statistics" }],
    
    // Desiri > Raw Material
    [RouteConstant.RawMaterialsPurchase]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "Raw Material Purchase" }],
    [RouteConstant.RawMaterialsPurchasedatatable]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "Raw Material Quality Check" }],
    [RouteConstant.rawmaterialInventory]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "Raw Material Inventory" }],
    [RouteConstant.rawmaterialMovementLog]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "Raw Material Movements" }],
    [RouteConstant.RawMaterailsReport]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "Raw Materials Inventory Value" }],
    [RouteConstant.RawMaterialCategories]: [{ label: "Desiri" }, { label: "Raw Material" }, { label: "View Raw material Category" }],
    
    // Desiri > Production & Batch Management
    [RouteConstant.oilProduction]: [{ label: "Desiri" }, { label: "Production & Batch Management" }, { label: "Oil Production Log" }],
    [RouteConstant.TestReports]: [{ label: "Desiri" }, { label: "Production & Batch Management" }, { label: "Generate Oil Test Report" }],
    [RouteConstant.GenerateProductCode]: [{ label: "Desiri" }, { label: "Production & Batch Management" }, { label: "Generate Batch Code" }],
    [RouteConstant.oilProductiondatatable]: [{ label: "Desiri" }, { label: "Production & Batch Management" }, { label: "Oil Production Records" }],
    
    // Desiri > Internal Complaints & QA
    [RouteConstant.InternalComplaintRegister]: [{ label: "Desiri" }, { label: "Internal Complaints & QA" }, { label: "Internal Complaints" }],
    [RouteConstant.InternalComplaintMain]: [{ label: "Desiri" }, { label: "Internal Complaints & QA" }, { label: "Internal Actions for QA" }],
    [RouteConstant.InternalComplaintActionMain]: [{ label: "Desiri" }, { label: "Internal Complaints & QA" }, { label: "View All Internal Complaints" }],
    
    // Reports > Sales Reports
    [RouteConstant.gstSalesReports]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "POS Target Mapping" }],
    [RouteConstant.cashDeposite]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "Collection Report" }],
    [RouteConstant.posReportDetails]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "POS Sales Report" }],
    [RouteConstant.CustomerExecutiveSalesReport]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "Customer Executive Sales Report" }],
    [RouteConstant.InvoicedBySalesReport]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "Invoice by Sales Report" }],
    [RouteConstant.customerReports]: [{ label: "Reports" }, { label: "Sales Reports" }, { label: "Customer Report" }],
    
    // Reports > Delivery Executive Reports
    [RouteConstant.deliveryAnalysis]: [{ label: "Reports" }, { label: "Delivery Executive Reports" }, { label: "Delivery Analysis" }],
    [RouteConstant.DeliveryExecutivePerformance]: [{ label: "Reports" }, { label: "Delivery Executive Reports" }, { label: "Delivery Executive Performance Report" }],
    
    // Reports > CRM Reports
    [RouteConstant.PerformanceReportforCE]: [{ label: "Reports" }, { label: "CRM Reports" }, { label: "Customer Executive Performance Report" }],
    [RouteConstant.CEDailyActivityReportModule]: [{ label: "Reports" }, { label: "CRM Reports" }, { label: "Customer Executive Daily Activity Report" }],
    [RouteConstant.CustomerExecutiveTargetMapping]: [{ label: "Reports" }, { label: "CRM Reports" }, { label: "Customer Executive Target Mapping" }],
    
    // Reports > Inventory & Production
    [RouteConstant.posInventoryReport]: [{ label: "Reports" }, { label: "Inventory & Production" }, { label: "POS Inventory Report" }],
    [RouteConstant.OilProductionSummaryReport]: [{ label: "Reports" }, { label: "Inventory & Production" }, { label: "Oil Production Monthly Summary Analysis" }],
    [RouteConstant.TestReporttest]: [{ label: "Reports" }, { label: "Inventory & Production" }, { label: "View Oil Test Report" }],
    [RouteConstant.RawMaterialHistory]: [{ label: "Reports" }, { label: "Inventory & Production" }, { label: "Raw Material Utilization Summary" }],
    
    // Vendors
    [RouteConstant.AllVendors]: [{ label: "Vendors" }, { label: "Add Vendors" }],
    
    // Settings & Access > Manage Access
    [RouteConstant.CreatingRole]: [{ label: "Settings & Access" }, { label: "Manage Access" }, { label: "Role Creation" }],
    [RouteConstant.RoleAssignment]: [{ label: "Settings & Access" }, { label: "Manage Access" }, { label: "Role Assignment" }],
    [RouteConstant.FeatureAccessAssign]: [{ label: "Settings & Access" }, { label: "Manage Access" }, { label: "Feature Access" }],
    
    // Settings & Access > Application Settings
    [RouteConstant.setting]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "Settings" }],
    [RouteConstant.courierCharges]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "Courier Charges" }],
    [RouteConstant.measurement]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "Measurement" }],
    [RouteConstant.banner]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "Banners" }],
    [RouteConstant.userPreferredDelivery]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "User Preferred Delivery" }],
    [RouteConstant.courierProvider]: [{ label: "Settings & Access" }, { label: "Application Settings" }, { label: "Courier Provider" }],
  };

  // Handle breadcrumb clicks
  useEffect(() => {
    const handleBreadcrumbClick = (label: string) => {
      // Map breadcrumb labels to their main tabs
      const tabMapping: Record<string, string> = {
        // Farm2Me sections
        "Farm2Me": "FARM2ME",
        "Orders & Billing": "FARM2ME",
        "External Complaints & QA": "FARM2ME",
        "Delivery & Route Management": "FARM2ME",
        "POS & Additional": "FARM2ME",
        "Customer & CRM": "FARM2ME",
        
        // Desiri sections
        "Desiri": "DESIRI",
        "Desiri Master Data": "DESIRI",
        "Raw Material": "DESIRI",
        "Production & Batch Management": "DESIRI",
        "Internal Complaints & QA": "DESIRI",
        
        // Reports sections
        "Reports": "REPORTS",
        "Sales Reports": "REPORTS",
        "Delivery Executive Reports": "REPORTS",
        "CRM Reports": "REPORTS",
        "Inventory & Production": "REPORTS",
        
        // Vendors
        "Vendors": "VENDORS",
        
        // Settings & Access
        "Settings & Access": "SETTINGS",
        "Manage Access": "SETTINGS",
        "Application Settings": "SETTINGS",
      };

      const mainTab = tabMapping[label] || "FARM2ME";
      
      // Open mega menu and set the appropriate tab
      setIsMegaMenuOpen(true);
      setActiveMainTab(mainTab as any);
    };

    setOnBreadcrumbClick?.(() => handleBreadcrumbClick);
  }, [setOnBreadcrumbClick, setActiveMainTab, setIsMegaMenuOpen]);

  const closeMegaAndNavigate = (route: string) => {
    setIsMegaMenuOpen(false);
    setSearchValue("");
    // Set breadcrumb based on route
    if (breadcrumbMap[route]) {
      setBreadcrumbModel?.(breadcrumbMap[route]);
    }
    setTimeout(() => navigate(route), 80);
  };

  // Admin Menu Structure
  const adminMenuStructure: MenuItem[] = [
    {
      id: "farm2me",
      name: "Farm2Me",
      icon: "pi pi-gift",
      items: [
        {
          id: "orders-billing",
          name: "Orders & Billing",
          items: [
            { id: "order-details", name: "Order Details", icon: "pi pi-shopping-cart", path: RouteConstant.newinvoice },
            { id: "generate-invoice", name: "Generate Invoice", icon: "pi pi-file-edit", path: RouteConstant.newGenerateInvoice },
            { id: "cancelled-orders", name: "Cancelled Orders", icon: "pi pi-times-circle", path: RouteConstant.canceledOrders },
            { id: "update-bin-no", name: "Update Bin No", icon: "pi pi-pencil", path: RouteConstant.packingOrders },
            { id: "view-bin-no-details", name: "View Bin No Details", icon: "pi pi-globe", path: RouteConstant.binOrders },
          ],
        },
        {
          id: "external-complaints-qa",
          name: "External Complaints & QA",
          items: [
            { id: "external-complaints", name: "External Complaints", icon: "pi pi-pencil", path: RouteConstant.ComplaintRegisters },
            { id: "external-actions-qa", name: "External Actions for QA", icon: "pi pi-list", path: RouteConstant.ComplaintMain },
            { id: "view-external-complaints", name: "View All External Complaints", icon: "pi pi-eye", path: RouteConstant.ComplaintActionMain },
            { id: "complaint-status", name: "Complaint Status", icon: "pi pi-exclamation-circle", path: RouteConstant.ViewAllStatus },
            { id: "complaint-types", name: "Complaint Types", icon: "pi pi-tags", path: RouteConstant.ComplaintType },
            { id: "complaint-category", name: "Complaint Category", icon: "pi pi-th-large", path: RouteConstant.viewAllCategories },
          ],
        },
        {
          id: "delivery-route-mgmt",
          name: "Delivery & Route Management",
          items: [
            { id: "smart-map", name: "Smart Map", icon: "pi pi-map", path: RouteConstant.Geomap },
            { id: "todays-map", name: "Today's Map", icon: "pi pi-search", path: RouteConstant.Geomapsearch },
            { id: "delivery-report", name: "Delivery Report", icon: "pi pi-file", path: RouteConstant.deliveryDetails },
            { id: "order-delivery-tracking", name: "Order Delivery Tracking", icon: "pi pi-truck", path: RouteConstant.OrderDeliverySummary },
            { id: "delivery-exec-daily", name: "Delivery Executive Daily Collection", icon: "pi pi-truck", path: RouteConstant.DeliveryCashMain },
            { id: "store-cash-collection", name: "Store Cash Collection", icon: "pi pi-box", path: RouteConstant.storeCashMain },
            { id: "accounts-cash-collection", name: "Accounts Cash Collection", icon: "pi pi-money-bill", path: RouteConstant.AccountsCashMain },
            { id: "where-is-my-cash", name: "Where is My Cash", icon: "pi pi-file", path: RouteConstant.CashReportMain },
            { id: "view-challan", name: "View Challan", icon: "pi pi-list", path: RouteConstant.ViewAllChallan },
            { id: "assign-route-code", name: "Assign Route Code", icon: "pi pi-compass", path: RouteConstant.assignroutecode },
            { id: "unassigned-orders", name: "Unassigned Orders", icon: "pi pi-exclamation-circle", path: RouteConstant.openorders },
            { id: "addcountry", name: "Country", icon: "pi pi-globe", path: RouteConstant.NewCountry },
            { id: "addstate", name: "State", icon: "pi pi-globe", path: RouteConstant.addState },
            { id: "adddistrict", name: "District", icon: "pi pi-globe", path: RouteConstant.addDistrict },
            { id: "addsubdistrict", name: "Sub District", icon: "pi pi-globe", path: RouteConstant.addSubdistrict },
            { id: "addarea", name: "Area and Pincode", icon: "pi pi-globe", path: RouteConstant.addArea },
            { id: "view-all-address", name: "View All Address", icon: "pi pi-list", path: RouteConstant.customerAddress },
            { id: "delivery-address", name: "Delivery Address", icon: "pi pi-send", path: RouteConstant.deliveryAddress },
            { id: "view-route-codes", name: "View Route Codes", icon: "pi pi-file-edit", path: RouteConstant.viewAllRouteCodes },
          ],
        },
        {
          id: "pos-additional",
          name: "POS & Additional",
          items: [
            { id: "view-all-products", name: "View All Products", icon: "pi pi-shopping-cart", path: RouteConstant.viewAllProducts },
            { id: "pos-inventory", name: "POS Inventory", icon: "pi pi-box", path: RouteConstant.positems },
            { id: "pos-movements", name: "POS Movements", icon: "pi pi-list", path: RouteConstant.posmovements },
            { id: "outward-challan", name: "Outward Challan", icon: "pi pi-list", path: RouteConstant.outwardChallan },
          ],
        },
        {
          id: "customer-crm",
          name: "Customer & CRM",
          items: [
            { id: "view-all-customers", name: "View All Customers", icon: "pi pi-list", path: RouteConstant.editUsers },
            { id: "assign-executives", name: "Assign Customer Executives", icon: "pi pi-user", path: RouteConstant.AssignExecutive },
            { id: "customer-followup", name: "Customer Follow Up", icon: "pi pi-phone", path: RouteConstant.customerFollowUpReport },
          ],
        },
      ],
    },
    {
      id: "desiri",
      name: "Desiri",
      icon: "pi pi-database",
      items: [
        {
          id: "desiri-master-data",
          name: "Desiri Master Data",
          items: [
            { id: "desiri-users", name: "Desiri User Profile", icon: "pi pi-users", path: RouteConstant.desiriUsers },
            { id: "desiri-bulls", name: " Bull", icon: "pi pi-list", path: RouteConstant.desiriBulls },
            { id: "desiri-gaana", name: " Gaana", icon: "pi pi-sliders-h", path: RouteConstant.desiriGaana },
            { id: "add-raw-materials", name: "Add Raw Materials", icon: "pi pi-shopping-cart", path: RouteConstant.ViewRawMaterials },
            { id: "vehicle-details", name: "Vehicle Details", icon: "pi pi-truck", path: RouteConstant.AddVehicle },
            { id: "roles-designations", name: "Roles & Designations", icon: "pi pi-briefcase", path: RouteConstant.desiriRoles },
            { id: "bull-statistics", name: "Bull Statistics", icon: "pi pi-chart-bar", path: RouteConstant.BullStatistics },
          ],
        },
        {
          id: "raw-material",
          name: "Raw Material",
          items: [
            { id: "rm-purchase", name: "Raw Material Purchase", icon: "pi pi-shopping-cart", path: RouteConstant.RawMaterialsPurchase },
            { id: "rm-quality-check", name: "Raw Material Quality Check", icon: "pi pi-check-circle", path: RouteConstant.RawMaterialsPurchasedatatable },
            { id: "rm-inventory", name: "Raw Material Inventory", icon: "pi pi-box", path: RouteConstant.rawmaterialInventory },
            { id: "rm-movements", name: "Raw Material Movements", icon: "pi pi-list", path: RouteConstant.rawmaterialMovementLog },
            { id: "rm-purchase-summary", name: "Raw Materials Inventory Value", icon: "pi pi-shopping-cart", path: RouteConstant.RawMaterailsReport },
            { id: "rm-categories", name: "View Raw material Category", icon: "pi pi-tags", path: RouteConstant.RawMaterialCategories }
          ],
        },
        {
          id: "production-batch-mgmt",
          name: "Production & Batch Management",
          items: [
            { id: "oil-production", name: "Oil Production Log", icon: "pi pi-book", path: RouteConstant.oilProduction },
            { id: "oil-test-report", name: "Generate Oil Test Report", icon: "pi pi-slack", path: RouteConstant.TestReports },
            { id: "generate-batch-code", name: "Generate Batch Code", icon: "pi pi-qrcode", path: RouteConstant.GenerateProductCode },
            { id: "oil-production-records", name: "Oil Production Records", icon: "pi pi-list", path: RouteConstant.oilProductiondatatable },
          ],
        },
        {
          id: "internal-complaints-qa",
          name: "Internal Complaints & QA",
          items: [
            { id: "internal-complaints", name: "Internal Complaints", icon: "pi pi-pencil", path: RouteConstant.InternalComplaintRegister },
            { id: "internal-actions-qa", name: "Internal Actions for QA", icon: "pi pi-list", path: RouteConstant.InternalComplaintMain },
            { id: "view-internal-complaints", name: "View All Internal Complaints", icon: "pi pi-eye", path: RouteConstant.InternalComplaintActionMain },
          ],
        },
      ],
    },
    {
      id: "reports",
      name: "Reports",
      icon: "pi pi-chart-bar",
      items: [
        {
          id: "sales-reports",
          name: "Sales Reports",
          items: [
            { id: "pos-target-mapping", name: "POS Target Mapping", icon: "pi pi-bullseye", path: RouteConstant.gstSalesReports },
            { id: "collection-report", name: "Collection Report", icon: "pi pi-wallet", path: RouteConstant.cashDeposite },
            { id: "gst-sales-report", name: "GST Sales Report", icon: "pi pi-file-invoice", path: RouteConstant.gstSalesReports },
            { id: "pos-sales-report", name: "POS Sales Report", icon: "pi pi-cash", path: RouteConstant.posReportDetails },
            { id: "customer-executive-sales", name: "Customer Executive Sales Report", icon: "pi pi-user-plus", path: RouteConstant.CustomerExecutiveSalesReport },
            { id: "invoice-by-sales-report", name: "Invoice by Sales Report", icon: "pi pi-file-invoice-dollar", path: RouteConstant.InvoicedBySalesReport },
            { id: "customer-report", name: "Customer Report", icon: "pi pi-user", path: RouteConstant.customerReports },
          ],
        },
        {
          id: "delivery-exec-reports",
          name: "Delivery Executive Reports",
          items: [
            { id: "delivery-analysis", name: "Delivery Analysis", icon: "pi pi-file", path: RouteConstant.deliveryAnalysis },
            { id: "delivery-exec-performance", name: "Delivery Executive Performance Report", icon: "pi pi-chart-bar", path: RouteConstant.DeliveryExecutivePerformance },
          ],
        },
        {
          id: "crm-reports",
          name: "CRM Reports",
          items: [
            { id: "ce-performance", name: "Customer Executive Performance Report", icon: "pi pi-chart-line", path: RouteConstant.PerformanceReportforCE },
            { id: "ce-daily-activity", name: "Customer Executive Daily Activity Report", icon: "pi pi-chart-line", path: RouteConstant.CEDailyActivityReportModule },
            { id: "ce-target-mapping", name: "Customer Executive Target Mapping", icon: "pi pi-dollar", path: RouteConstant.CustomerExecutiveTargetMapping },
          ],
        },
        {
          id: "inventory-production-reports",
          name: "Inventory & Production",
          items: [
            { id: "pos-inventory-report", name: "POS Inventory Report", icon: "pi pi-list", path: RouteConstant.posInventoryReport },
            { id: "oil-production-summary", name: "Oil Production Monthly Summary Analysis", icon: "pi pi-chart-bar", path: RouteConstant.OilProductionSummaryReport },
            { id: "view-oil-test-report", name: "View Oil Test Report", icon: "pi pi-list", path: RouteConstant.TestReporttest },
            { id: "raw-material-utilization", name: "Raw Material Utilization Summary", icon: "pi pi-chart-bar", path: RouteConstant.RawMaterialHistory },
          ],
        },
      ],
    },
    {
      id: "vendors",
      name: "Vendors",
      icon: "pi pi-building",
      items: [
        { id: "add-vendors", name: "Add Vendors", icon: "pi pi-building", path: RouteConstant.AllVendors },
      ],
    },
    {
      id: "settings",
      name: "Settings & Access",
      icon: "pi pi-cog",
      items: [
        {
          id: "manage-access",
          name: "Manage Access",
          items: [
            { id: "role-creation", name: "Role Creation", icon: "pi pi-users", path: RouteConstant.CreatingRole },
            { id: "role-assignment", name: "Role Assignment", icon: "pi pi-users", path: RouteConstant.RoleAssignment },
            { id: "feature-access", name: "Feature Access", icon: "pi pi-users", path: RouteConstant.FeatureAccessAssign },
          ],
        },
        {
          id: "application-settings",
          name: "Application Settings",
          items: [
            { id: "settings", name: "Settings", icon: "pi pi-cog", path: RouteConstant.setting },
            { id: "courier-charges", name: "Courier Charges", icon: "pi pi-cog", path: RouteConstant.courierCharges },
            { id: "measurement", name: "Measurement", icon: "pi pi-cog", path: RouteConstant.measurement },
            { id: "banners", name: "Banners", icon: "pi pi-cog", path: RouteConstant.banner },
            { id: "user-preferred-delivery", name: "User Preferred Delivery", icon: "pi pi-cog", path: RouteConstant.userPreferredDelivery },
            { id: "courier-provider", name: "Courier Provider", icon: "pi pi-cog", path: RouteConstant.courierProvider },
          ],
        },
      ],
    },
  ];

  const customerMenuItems: MenuItem[] = [
    { id: "profile", name: "My Profile", icon: "pi pi-user", path: RouteConstant.profile },
    { id: "addresses", name: "My Addresses", icon: "pi pi-id-card", path: RouteConstant.userAddresses },
    { id: "orders", name: "My Orders", icon: "pi pi-shopping-cart", path: RouteConstant.myOrders },
  ];

  const customerExecutiveMenuItems: MenuItem[] = [
    { id: "profile", name: "My Profile", icon: "pi pi-user", path: RouteConstant.profile },
    { id: "orders", name: "My Orders", icon: "pi pi-shopping-cart", path: RouteConstant.myOrders },
    { id: "customer-followup", name: "Customer Follow Up", icon: "pi pi-phone", path: RouteConstant.customerFollowUpReport },
  ];

  const driverMenuItems: MenuItem[] = [
    { id: "profile", name: "My Profile", icon: "pi pi-user", path: RouteConstant.profile },
    { id: "orders", name: "My Orders", icon: "pi pi-shopping-cart", path: RouteConstant.myOrders },
    { id: "delivery-report", name: "Delivery Report", icon: "pi pi-file", path: RouteConstant.deliveryDetails },
  ];

  const combinedMenus = useMemo((): MenuItem[] => {
    return [
      ...adminMenuStructure,
      ...customerMenuItems,
      ...customerExecutiveMenuItems,
      ...driverMenuItems,
    ];
  }, []);

  // Build searchable menu items
  const allSearchableMenuItems = useMemo(() => {
    const items: SearchMenuItem[] = [];

    const extractItems = (menuItems: MenuItem[], mainTab: MainTabKey, category: string = "") => {
      menuItems.forEach((item) => {
        if ("path" in item && item.path) {
          items.push({
            id: item.id,
            name: item.name,
            path: item.path,
            mainTab,
            category,
          });
        }
        if ("items" in item && item.items) {
          extractItems(item.items, mainTab, item.name);
        }
      });
    };

    adminMenuStructure.forEach((mainItem) => {
      const mainTab = mainItem.id.toUpperCase() as MainTabKey;
      if ("items" in mainItem && mainItem.items) {
        extractItems(mainItem.items, mainTab, "");
      }
    });

    return items;
  }, [adminMenuStructure]);

  // Search function
  const searchMenuItems = (event: AutoCompleteCompleteEvent) => {
    setTimeout(() => {
      let _filtered: SearchMenuItem[];

      if (!event.query.trim().length) {
        _filtered = [...allSearchableMenuItems];
      } else {
        _filtered = allSearchableMenuItems.filter((item) => {
          return item.name.toLowerCase().includes(event.query.toLowerCase());
        });
      }

      setFilteredMenuItems(_filtered);
    }, 250);
  };

  // Handle selection
  const handleSearchSelect = (e: any) => {
    const selectedItem: SearchMenuItem = e.value;
    if (selectedItem && selectedItem.path) {
      // Switch to the correct tab
      setActiveMainTab(selectedItem.mainTab);
      // Clear search
      setSearchValue("");
      // Navigate after short delay
      setTimeout(() => {
        closeMegaAndNavigate(selectedItem.path!);
      }, 100);
    }
  };

  // Custom item template
  const searchItemTemplate = (item: SearchMenuItem) => {
    return (
      <div className="search-result-item">
        <div className="search-result-name">{item.name}</div>
        <div className="search-result-meta">
          <span className="search-result-tab">{item.mainTab}</span>
          {item.category && (
            <>
              <span>•</span>
              <span className="search-result-category">{item.category}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  const normalize = (p: string) => (p || "").replace(/\/+$/, "");

  const isHomePage = normalize(location.pathname) === normalize(RouteConstant.products);

  const itemsLang = [
    { label: "English", command: () => i18n.changeLanguage("en") },
    { label: "عربی", command: () => i18n.changeLanguage("ar") },
    { label: "ಕನ್ನಡ", command: () => i18n.changeLanguage("kn") },
    { label: "हिंदी", command: () => i18n.changeLanguage("hn") },
  ];

  useEffect(() => {
    const handleDocumentClick = (e: any) => {
      try {
        if (topbarmenuRef.current && !topbarmenuRef.current.contains(e.target)) {
          setIsMenuVisible(false);
        }
        if (
          topbarmenubuttonRef.current &&
          topbarmenubuttonRef.current.contains(e.target)
        ) {
          setIsMenuVisible(!isMenuVisible);
        }
      } catch (ex: any) {
        console.error(ex);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [isMenuVisible]);

  useImperativeHandle(ref, () => ({
    menubutton: menubuttonRef.current,
    topbarmenu: topbarmenuRef.current,
    topbarmenubutton: topbarmenubuttonRef.current,
  }));

  const [searchQuery, setSearchQuery] = useState("");
  const [animate, setAnimate] = useState(false);

  const handleSearchChange = (e: any) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Navigate to products with search query
    if (query.trim()) {
      navigate(`${RouteConstant.products}?search=${encodeURIComponent(query)}`);
    } else {
      setBreadcrumbModel?.([]);
      navigate(RouteConstant.products);
    }
  };

  useEffect(() => {
    if (count > 0) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [count]);

  // Handle click outside search dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        !(event.target as HTMLElement).closest('.topbar-search-icon-btn')
      ) {
        setIsSearchModalOpen(false);
      }
    };

    if (isSearchModalOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isSearchModalOpen]);

  const canSeeFarm2Me = RoleService.isAdmin();
  const canSeeDesiri = RoleService.isAdmin();
  const canSeeReports = RoleService.isAdmin();
  const canSeeVendors = RoleService.isAdmin();
  const canSeeChallan = RoleService.isAdmin();
  const canSeeSettings = RoleService.isAdmin();
  const canSeeAccountInfo =
    RoleService.isAdmin() ||
    RoleService.isCustomer() ||
    RoleService.isDriver() ||
    RoleService.isCustomerExecutive();

  const MegaLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <button className="mega-link" type="button" onClick={onClick}>
      {label}
    </button>
  );

  const MegaSubTitle = ({ label }: { label: string }) => (
    <div className="mega-subtitle">{label}</div>
  );

  const renderFarm2Me = () => {
    if (!canSeeFarm2Me) return null;
    return (
      <div className="mega-grid-4">
        <div>
          <div className="mega-section-title">Orders & Billing</div>
          <MegaLink label="Order Details" onClick={() => closeMegaAndNavigate(RouteConstant.newinvoice)} />
          <MegaLink label="Generate Invoice" onClick={() => closeMegaAndNavigate(RouteConstant.newGenerateInvoice)} />
          <MegaLink label="Cancelled Orders" onClick={() => closeMegaAndNavigate(RouteConstant.canceledOrders)} />
          <MegaSubTitle label="View Package No" />
          <MegaLink label="Update Bin No" onClick={() => closeMegaAndNavigate(RouteConstant.packingOrders)} />
          <MegaLink label="View Bin No Details" onClick={() => closeMegaAndNavigate(RouteConstant.binOrders)} />
        </div>

        <div>
          <div className="mega-section-title">External Complaints & QA</div>
          <MegaLink label="External Complaints" onClick={() => closeMegaAndNavigate(RouteConstant.ComplaintRegisters)} />
          <MegaLink label="External Actions for QA" onClick={() => closeMegaAndNavigate(RouteConstant.ComplaintMain)} />
          <MegaLink label="View All External Complaints" onClick={() => closeMegaAndNavigate(RouteConstant.ComplaintActionMain)} />
          <MegaSubTitle label="Complaints Info" />
          <MegaLink label="Complaint Status" onClick={() => closeMegaAndNavigate(RouteConstant.ViewAllStatus)} />
          <MegaLink label="Complaint Types" onClick={() => closeMegaAndNavigate(RouteConstant.ComplaintType)} />
          <MegaLink label="Complaint Category" onClick={() => closeMegaAndNavigate(RouteConstant.viewAllCategories)} />
        </div>

        <div>
          <div className="mega-section-title">Delivery & Route Management</div>
          <MegaSubTitle label="Smart Route" />
          <MegaLink label="Smart Map" onClick={() => closeMegaAndNavigate(RouteConstant.Geomap)} />
          <MegaLink label="Today's Map" onClick={() => closeMegaAndNavigate(RouteConstant.Geomapsearch)} />
          <MegaSubTitle label="Delivery Info" />
          <MegaLink label="Delivery Report" onClick={() => closeMegaAndNavigate(RouteConstant.deliveryDetails)} />
          <MegaLink label="Order Delivery Tracking" onClick={() => closeMegaAndNavigate(RouteConstant.OrderDeliverySummary)} />
          <MegaSubTitle label="Delivery Cash" />
          <MegaLink label="Delivery Executive Daily Collection" onClick={() => closeMegaAndNavigate(RouteConstant.DeliveryCashMain)} />
          <MegaLink label="Store Cash Collection" onClick={() => closeMegaAndNavigate(RouteConstant.storeCashMain)} />
          <MegaLink label="Accounts Cash Collection" onClick={() => closeMegaAndNavigate(RouteConstant.AccountsCashMain)} />
          <MegaLink label="Where is My Cash" onClick={() => closeMegaAndNavigate(RouteConstant.CashReportMain)} />
      
          <MegaSubTitle label="Assign Code" />
          <MegaLink label="Assign Route Code" onClick={() => closeMegaAndNavigate(RouteConstant.assignroutecode)} />
          <MegaLink label="Unassigned Orders" onClick={() => closeMegaAndNavigate(RouteConstant.openorders)} />
          <MegaSubTitle label="View Addresses" />
          <MegaLink label="View All Address" onClick={() => closeMegaAndNavigate(RouteConstant.customerAddress)} />
          <MegaLink label="Delivery Address" onClick={() => closeMegaAndNavigate(RouteConstant.deliveryAddress)} />
          <MegaLink label="View Route Codes" onClick={() => closeMegaAndNavigate(RouteConstant.viewAllRouteCodes)} />
          
          <MegaLink label="Country" onClick={() => closeMegaAndNavigate(RouteConstant.NewCountry)} />
          <MegaLink label="State" onClick={() => closeMegaAndNavigate(RouteConstant.addState)} />
          <MegaLink label="District" onClick={() => closeMegaAndNavigate(RouteConstant.addDistrict)} />
          <MegaLink label="Sub District" onClick={() => closeMegaAndNavigate(RouteConstant.addSubdistrict)} />
          <MegaLink label="Area and Pincode" onClick={() => closeMegaAndNavigate(RouteConstant.addArea)} />
        </div>

        <div>
          <div className="mega-section-title">POS & Additional</div>
          <MegaLink label="View All Products" onClick={() => closeMegaAndNavigate(RouteConstant.viewAllProducts)} />
          <MegaLink label="POS Inventory" onClick={() => closeMegaAndNavigate(RouteConstant.positems)} />
          <MegaLink label="POS Movements" onClick={() => closeMegaAndNavigate(RouteConstant.posmovements)} />
          <MegaLink label="Outward Challan" onClick={() => closeMegaAndNavigate(RouteConstant.outwardChallan)} />
          <Divider />
          <div className="mega-section-title">Customer & CRM</div>
          <MegaLink label="View All Customers" onClick={() => closeMegaAndNavigate(RouteConstant.editUsers)} />
          <MegaSubTitle label="CRM" />
          <MegaLink label="Assign Customer Executives" onClick={() => closeMegaAndNavigate(RouteConstant.AssignExecutive)} />
          <MegaLink label="Customer Follow Up" onClick={() => closeMegaAndNavigate(RouteConstant.customerFollowUpReport)} />
        </div>
      </div>
    );
  };

  const renderDesiri = () => {
    if (!canSeeDesiri) return null;
    return (
      <div className="mega-grid-single">
        <div>
          <div className="mega-section-title">Desiri Master Data</div>
          <MegaLink label="Desiri User Profile" onClick={() => closeMegaAndNavigate(RouteConstant.desiriUsers)} />
          <MegaLink label=" Bull" onClick={() => closeMegaAndNavigate(RouteConstant.desiriBulls)} />
          <MegaLink label=" Gaana" onClick={() => closeMegaAndNavigate(RouteConstant.desiriGaana)} />
          <MegaLink label="Vehicle Details" onClick={() => closeMegaAndNavigate(RouteConstant.AddVehicle)} />
          <MegaLink label="Roles & Designations" onClick={() => closeMegaAndNavigate(RouteConstant.desiriRoles)} />
        </div>

        <div>
          <div className="mega-section-title">Raw Material</div>
          <MegaLink label="Raw Material Purchase" onClick={() => closeMegaAndNavigate(RouteConstant.RawMaterialsPurchase)} />
          <MegaLink label="Add Raw Materials" onClick={() => closeMegaAndNavigate(RouteConstant.ViewRawMaterials)} />
          <MegaLink label="Raw Material Quality Check" onClick={() => closeMegaAndNavigate(RouteConstant.RawMaterialsPurchasedatatable)} />
          <MegaLink label="Raw Material Inventory" onClick={() => closeMegaAndNavigate(RouteConstant.rawmaterialInventory)} />
          <MegaLink label="Raw Material Movements" onClick={() => closeMegaAndNavigate(RouteConstant.rawmaterialMovementLog)} />
          <MegaLink label="Raw Materials Inventory Value" onClick={() => closeMegaAndNavigate(RouteConstant.RawMaterailsReport)} />
          <MegaLink label="View Raw material Category" onClick={() => closeMegaAndNavigate(RouteConstant.RawMaterialCategories)} />
        </div>

        <div>
          <div className="mega-section-title">Production & Batch Management</div>
          <MegaLink label="Oil Production Log" onClick={() => closeMegaAndNavigate(RouteConstant.oilProduction)} />
          <MegaLink label="Generate Oil Test Report" onClick={() => closeMegaAndNavigate(RouteConstant.TestReports)} />
          <MegaLink label="Generate Batch Code" onClick={() => closeMegaAndNavigate(RouteConstant.GenerateProductCode)} />
          <MegaLink label="Bull Statistics" onClick={() => closeMegaAndNavigate(RouteConstant.BullStatistics)} />
          <MegaLink label="Oil Production Records" onClick={() => closeMegaAndNavigate(RouteConstant.oilProductiondatatable)} />
          <MegaLink label="Oil Production Summary Analysis" onClick={() => closeMegaAndNavigate(RouteConstant.OilProductionSummaryReport)} />

          <Divider />
          <div className="mega-section-title">Internal Complaints & QA</div>
          <MegaLink label="Internal Complaints" onClick={() => closeMegaAndNavigate(RouteConstant.InternalComplaintRegister)} />
          <MegaLink label="Internal Actions for QA" onClick={() => closeMegaAndNavigate(RouteConstant.InternalComplaintMain)} />
          <MegaLink label="View All Internal Complaints" onClick={() => closeMegaAndNavigate(RouteConstant.InternalComplaintActionMain)} />
        </div>
      </div>
    );
  };

  const renderReports = () => {
    if (!canSeeReports) return null;
    return (
      <div className="mega-grid-single">
        <div>
          <div className="mega-section-title">Sales Reports</div>
          <MegaLink label="POS Target Mapping" onClick={() => closeMegaAndNavigate(RouteConstant.gstSalesReports)} />
            <MegaLink label="Collection Report" onClick={() => closeMegaAndNavigate(RouteConstant.cashDeposite)} />
              <MegaLink label="GST Sales Report" onClick={() => closeMegaAndNavigate(RouteConstant.gstSalesReports)} />
                <MegaLink label="POS Report Details" onClick={() => closeMegaAndNavigate(RouteConstant.posReportDetails)} />
                  <MegaLink label="Customer Executive Sales Report" onClick={() => closeMegaAndNavigate(RouteConstant.CustomerExecutiveSalesReport)} />
                    <MegaLink label="Invoice By Sales report" onClick={() => closeMegaAndNavigate(RouteConstant.InvoicedBySalesReport)} />

          <MegaLink label="Customer Report" onClick={() => closeMegaAndNavigate(RouteConstant.customerReports)} />
        </div>

        <div>
          <div className="mega-section-title">Delivery Executive Reports</div>
          <MegaLink label="Delivery Analysis" onClick={() => closeMegaAndNavigate(RouteConstant.deliveryAnalysis)} />
          <MegaLink label="Delivery Executive Performance Report" onClick={() => closeMegaAndNavigate(RouteConstant.DeliveryExecutivePerformance)} />
        </div>

        <div>
          <div className="mega-section-title">CRM Reports</div>
          <MegaLink label="Customer Executive Performance Report" onClick={() => closeMegaAndNavigate(RouteConstant.PerformanceReportforCE)} />
          <MegaLink label="Customer Executive Daily Activity Report" onClick={() => closeMegaAndNavigate(RouteConstant.CEDailyActivityReportModule)} />
          <MegaLink label="Customer Executive Target Mapping" onClick={() => closeMegaAndNavigate(RouteConstant.CustomerExecutiveTargetMapping)} />
          <Divider />
          <div className="mega-section-title">Inventory & Production</div>
          <MegaLink label="POS Inventory Report" onClick={() => closeMegaAndNavigate(RouteConstant.posInventoryReport)} />
          <MegaLink label="Oil Production Summary Analysis" onClick={() => closeMegaAndNavigate(RouteConstant.OilProductionSummaryReport)} />
          <MegaLink label="Oil Test Report" onClick={() => closeMegaAndNavigate(RouteConstant.TestReporttest)} />
          <MegaLink label="Raw Material Utilization Summary" onClick={() => closeMegaAndNavigate(RouteConstant.RawMaterialHistory)} />
        </div>
      </div>
    );
  };

  const renderVendors = () => {
    if (!canSeeVendors) return null;
    return (
      <div className="mega-grid-single">
        <div>
          <div className="mega-section-title">Vendors</div>
          <MegaLink label="Add Vendors" onClick={() => closeMegaAndNavigate(RouteConstant.AllVendors)} />
        </div>
      </div>
    );
  };

  const renderChallan = () => {
    if (!canSeeChallan) return null;
    return (
      <div className="mega-grid-single">
        <div>
          <div className="mega-section-title">Challan</div>
          <MegaLink label="View Challan" onClick={() => closeMegaAndNavigate(RouteConstant.ViewAllChallan)} />
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    if (!canSeeSettings) return null;
    return (
      <div className="mega-grid-single">
        <div>
          <div className="mega-section-title">Manage Access</div>
          <MegaLink label="Role Creation" onClick={() => closeMegaAndNavigate(RouteConstant.CreatingRole)} />
          <MegaLink label="Role Assignment" onClick={() => closeMegaAndNavigate(RouteConstant.RoleAssignment)} />
          <MegaLink label="Feature Access" onClick={() => closeMegaAndNavigate(RouteConstant.FeatureAccessAssign)} />
        </div>

        <div>
          <div className="mega-section-title">Application Settings</div>
          <MegaLink label="Courier Charges" onClick={() => closeMegaAndNavigate(RouteConstant.courierCharges)} />
          <MegaLink label="Measurement" onClick={() => closeMegaAndNavigate(RouteConstant.measurement)} />
          <MegaLink label="Banners" onClick={() => closeMegaAndNavigate(RouteConstant.banner)} />
          <MegaLink label="User Preferred Delivery" onClick={() => closeMegaAndNavigate(RouteConstant.userPreferredDelivery)} />
          <MegaLink label="Courier Provider" onClick={() => closeMegaAndNavigate(RouteConstant.courierProvider)} /> 
        </div>
      </div>
    );
  };

  const renderActiveContent = () => {
    switch (activeMainTab) {
      case "FARM2ME":
        return renderFarm2Me();
      case "DESIRI":
        return renderDesiri();
      case "REPORTS":
        return renderReports();
      case "VENDORS":
        return renderVendors();
      case "CHALLAN":
        return renderChallan();
      case "SETTINGS":
        return renderSettings();
      default:
        return null;
    }
  };

  const renderMegaMenu = () => {
    if (!isMegaMenuOpen || !isUserLoggedIn) return null;

    const mainTabs: { key: MainTabKey; label: string; visible: boolean }[] = [
      { key: "FARM2ME", label: "Farm2Me", visible: canSeeFarm2Me },
      { key: "DESIRI", label: "Desiri", visible: canSeeDesiri },
      { key: "REPORTS", label: "Reports", visible: canSeeReports },
      { key: "VENDORS", label: "Vendors", visible: canSeeVendors },
      { key: "CHALLAN", label: "Challan", visible: canSeeChallan },
      { key: "SETTINGS", label: "Settings & Access", visible: canSeeSettings },
    ];

    const visibleTabs = mainTabs.filter((t) => t.visible);
    if (!visibleTabs.find((t) => t.key === activeMainTab)) {
      if (visibleTabs.length > 0) {
        setActiveMainTab(visibleTabs[0].key);
      }
    }

    return (
      <div className="mega-backdrop" onClick={() => { setIsMegaMenuOpen(false); setSearchValue(""); }}>
        <div className="mega-container" onClick={(e) => e.stopPropagation()}>
          <div className="mega-main-tabs">
            <div className="mega-tabs-left">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={classNames("mega-main-tab", {
                    "mega-main-tab-active": activeMainTab === tab.key,
                  })}
                  onClick={() => { setActiveMainTab(tab.key); setSearchValue(""); }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mega-search-wrapper">
              <div className="mega-search-container">
                <i className="pi pi-search mega-search-icon" />
                <AutoComplete
                  className="mega-search-autocomplete"
                  value={searchValue}
                  suggestions={filteredMenuItems}
                  completeMethod={searchMenuItems}
                  field="name"
                  placeholder="Search menu..."
                  onChange={(e) => setSearchValue(e.value)}
                  onSelect={handleSearchSelect}
                  itemTemplate={searchItemTemplate}
                  forceSelection={false}
                  dropdown={false}
                />
              </div>
            </div>

            <button
              className="mega-close-btn"
              type="button"
              onClick={() => { setIsMegaMenuOpen(false); setSearchValue(""); }}
              title="Close menu"
            >
              <i className="pi pi-times" />
            </button>
          </div>

          <div className="mega-body">{renderActiveContent()}</div>
        </div>
      </div>
    );
  };

  const handleAccountMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    menuLeft.current?.toggle(e);
  };

  return (
    <>
      <style>{MEGA_MENU_CSS}</style>

      <Toast ref={toastRef} />

      <div className="layout-topbar">
        {/* Logo */}
        <div
          className="layout-topbar-logo"
          onClick={() => {
            setBreadcrumbModel?.([]);
            navigate(RouteConstant.products);
          }}
        >
          <img src={process.env.PUBLIC_URL + "/assests/images/logo.png"} alt="Farm2Me" />
        </div>

        {/* Hamburger menu button (mobile) */}
        <button
          ref={topbarmenubuttonRef}
          type="button"
          className="layout-topbar-menu-button"
          onClick={() => setIsMenuVisible(!isMenuVisible)}
        >
          <i className="pi pi-bars" />
        </button>

        {/* Centered Navigation Menu */}
        <nav
          ref={topbarmenuRef}
          className={classNames("layout-topbar-nav", {
            "layout-topbar-nav-open": isMenuVisible,
          })}
        >
        
        </nav>

        {/* Right Side: Search, Customer Name/Label, Login, Cart */}
        <div className="layout-topbar-right">
          {/* Export and WhatsApp Buttons - Desktop Only */}
          <div className="topbar-action-buttons desktop-only" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button
              label="Export Products"
              icon="pi pi-download"
              className="p-button-md w-auto border-round"
              onClick={handleProductPriceExport}
              text
              raised
              tooltip="Export Product List"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: '#16a34a', fontWeight: '500' }}
            />
            <Button
              icon="pi pi-whatsapp"
              className="p-button-md p-button-icon-only border-round"
              onClick={() => setShowWhatsAppModal(true)}
              tooltip="Send via WhatsApp"
              tooltipOptions={{ position: 'bottom' }}
              text
              raised
              style={{ color: '#25d366' }}
            />
          </div>

          {/* Home Icon - Visible when NOT on home page */}
          {!isHomePage && (
            <button
              className="topbar-icon-btn topbar-home-btn"
              onClick={() => {
                setBreadcrumbModel?.([]);
                navigate(RouteConstant.products);
              }}
              aria-label="Home"
              title="Back to Home"
            >
              <i className="pi pi-home" />
            </button>
          )}

          {/* Search Bar - Desktop */}
          <div className="topbar-search topbar-search-desktop">
            <i className="pi pi-search" />
            <input
              type="search"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => {
                const query = e.target.value;
                setSearchQuery(query);
                if (query.trim()) {
                  navigate(`${RouteConstant.products}?search=${encodeURIComponent(query)}`);
                } else {
                  setBreadcrumbModel?.([]);
                  navigate(RouteConstant.products);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setSearchQuery("");
                  setBreadcrumbModel?.([]);
                  navigate(RouteConstant.products);
                }
              }}
            />
          </div>

          {/* Search Icon Button - Mobile */}
          <button
            className="topbar-icon-btn topbar-search-icon-btn"
            onClick={() => setIsSearchModalOpen(true)}
            aria-label="Search"
            title="Search products"
          >
            <i className="pi pi-search" />
          </button>

          {/* Login / Account */}
          {!isUserLoggedIn ? (
            <button
              className="topbar-login-btn"
              onClick={() => navigate(RouteConstant.login)}
            >
              <i className="pi pi-sign-in" />
              <span>Login</span>
            </button>
          ) : (
            <div
              className="user-profile-wrapper"
            >
              <button
                className="topbar-account-btn"
                aria-label="Account"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  menuLeft.current?.toggle(e);
                }}
              >
                <i className="pi pi-user" />
              </button>
              <span 
                className="user-name-below"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  menuLeft.current?.toggle(e);
                }}
              >
                {currentUser?.name ? currentUser.name.split(' ')[0] : "User"}
              </span>
            </div>
          )}

          {/* Cart - Always visible but styled differently for mobile */}
          <button
            className="topbar-icon-btn topbar-cart-btn"
            onClick={() => navigate(RouteConstant.basket)}
            aria-label="Cart"
          >
            <i className={`pi pi-shopping-cart ${animate ? "zoomin" : ""}`} />
            {count > 0 && <Badge value={count} severity="danger" />}
          </button>

          {/* Menu Button for Admin - Opens Mega Menu */}
          {isUserLoggedIn && !RoleService.isCustomer() && (
            <button
              className="topbar-icon-btn topbar-menu-btn"
              onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
              aria-label="Menu"
              title="Open Menu"
            >
              <i className="pi pi-bars" />
            </button>
          )}
        </div>
      </div>

      {/* Admin/User Menu - TieredMenu */}
      {isUserLoggedIn && (
        <Menu
          model={([
            {
              label: currentUser?.name ? `Welcome ${currentUser.name.split(' ')[0]}` : "User",
              icon: "pi pi-user",
            },
            { separator: true },
            {
              label: t("layout_apptopbar_myprofile"),
              icon: "pi pi-solid pi-user",
              command: () => navigate(RouteConstant.profile),
            },
            {
              label: t("layout_apptopbar_myaddresses"),
              icon: "pi pi-id-card",
              command: () => navigate(RouteConstant.userAddresses),
            },
            {
              label: "My Orders",
              icon: "pi pi-shopping-cart",
              command: () => navigate(RouteConstant.myOrders),
            },
            RoleService.isCustomer() ? {
              label: "Complaint Register",
              icon: "pi pi-list",
              items: [
                {
                  label: "Service Complaint Register",
                  icon: "pi pi-pencil",
                  command: () => setComplaintDialogVisible(true),
                },
                {
                  label: "View All Complaints",
                  icon: "pi pi-list",
                  command: () => navigate(RouteConstant.CustomerComplaintMain),
                },
              ],
            } : null,
            RoleService.isCustomer() ? {
              label: "Call Me Back",
              icon: "pi pi-bell",
              command: () => handleCallMeBack(),
            } : null,
            { separator: true },
            {
              label: t("layout_apptopbar_logout"),
              icon: "pi pi-sign-out",
              command: () => {
                AuthService.logout();
                navigate(RouteConstant.login);
              },
            },
          ] as any).filter(Boolean)}
          popup
          ref={menuLeft}
          id="popup_menu_left"
        />
      )}

      {/* Mega Menu Dialog */}
      {renderMegaMenu()}

      {/* Executive Info Overlay */}
      <OverlayPanel ref={executiveOverlayRef} style={{ width: "250px" }}>
        {executiveInfo ? (
          <Card className="text-center surface-100 border-round shadow-2">
            <div className="flex flex-column align-items-center gap-1">
              <Avatar
                icon="pi pi-user"
                size="large"
                className="bg-blue-80 text-blue-500"
                shape="circle"
              />
              <div className="mt-2 text-xl font-semibold text-gray-900">
                {executiveInfo.executiveName}
              </div>
              <div className="text-sm text-gray-500">Sales Advisor</div>
              <Divider className="my-2 w-10" />
              <div className="flex align-items-center gap-2">
                <i className="pi pi-phone text-green-700" />
                <a
                  href={`tel:${executiveInfo.executivePhoneNumber}`}
                  className="text-primary font-semibold no-underline"
                >
                  {executiveInfo.executivePhoneNumber}
                </a>
              </div>
              <Button
                icon="pi pi-bell"
                label="Call Me Back"
                className="mt-3 p-button-sm p-button-text p-button-warning"
                onClick={handleCallMeBack}
              />
            </div>
          </Card>
        ) : (
          <Card className="p-3 text-center surface-100 border-round shadow-2">
            <div className="text-gray-500 italic">
              No executive assigned yet
            </div>
          </Card>
        )}
      </OverlayPanel>

      <CustomerServComplaintRegister
        visible={complaintDialogVisible}
        onHide={() => setComplaintDialogVisible(false)}
        onSuccess={() => {
          // Optional: Refresh complaints list or show success message
        }}
      />

      {/* WhatsApp Modal Dialog */}
      <Dialog
        header="Send via WhatsApp"
        visible={showWhatsAppModal}
        onHide={() => setShowWhatsAppModal(false)}
        style={{ width: '24rem' }}
        className="p-dialog-sm"
        modal
        breakpoints={{ '960px': '90vw' }}
      >
        <div className="flex flex-column gap-2 p-2">
          <label htmlFor="phone" className="text-sm font-medium mb-1">Recipient Mobile Number</label>

          <div className="flex align-items-center justify-content-between gap-2">
            <div className="flex align-items-center gap-1">
              <span className="px-2 py-1 border-round border-1 surface-border bg-white text-sm">+91</span>
              <InputText
                id="phone"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="10-digit number"
                keyfilter="int"
                maxLength={10}
                className="p-inputtext-sm border-1 surface-border"
                style={{ width: '10rem', fontSize: '0.8rem' }}
              />
            </div>

            <Button
              label="Send"
              icon="pi pi-send"
              className="p-button-sm border-round"
              onClick={() => handleWhatsAppReferral()}
              disabled={!recipientPhone || recipientPhone.length !== 10}
              text
              raised
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
            />
          </div>

          {!isUserLoggedIn && (
            <small className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              You're sending this as <strong>"Your friend"</strong>. Log in to personalize your name.
            </small>
          )}
        </div>
      </Dialog>

      {/* Search Dropdown - Mobile */}
      {isSearchModalOpen && (
        <div className="search-dropdown-bar" ref={searchDropdownRef}>
          <div className="search-dropdown-content">
            <i className="pi pi-search search-dropdown-icon" />
            <input
              type="search"
              placeholder="Search products..."
              value={searchQuery}
              autoFocus
              onChange={(e) => {
                const query = e.target.value;
                setSearchQuery(query);
                if (query.trim()) {
                  navigate(`${RouteConstant.products}?search=${encodeURIComponent(query)}`);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsSearchModalOpen(false);
                  setSearchQuery("");
                  setBreadcrumbModel?.([]);
                  navigate(RouteConstant.products);
                }
              }}
              className="search-dropdown-input"
            />
          </div>
        </div>
      )}
    </>
  );
});

AppTopbar.displayName = "AppTopbar";

export default AppTopbar;
